import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  ArrowLeftRight,
  FileDown,
  ImagePlus,
  LayoutGrid,
  Loader2,
  Package,
  Search,
  Tag,
  Trash2,
  X,
} from "lucide-react";
import qz from "qz-tray";
import { createLabelPrinterConfig } from "../../utils/qzTray";
import {
  buildCustomBoardLabelLines,
  buildTsplFromLines,
  downloadLabelsPdf,
} from "../../utils/labelPdf";
import { API_BASE_URL_V2 } from "../../api/URL_Header";
import { fetchDesignByCode, fetchDesignById } from "../../api/DesignAPIs";
import { fetchOrderById, searchOrdersByPhone } from "../../api/OrderAPIs";
import { generateCustomPrintPdf } from "../../api/PrintSheetAPIs";

const COLUMNS = 6;
const ROWS = 2;
const SLOT_COUNT = COLUMNS * ROWS;

const emptyBoard = () => Array.from({ length: SLOT_COUNT }, () => null);

const isSlotFilled = (slot) => !!(slot?.designCode || slot?.replacementFile);

const revokePreview = (slot) => {
  if (slot?.replacementPreviewUrl) {
    URL.revokeObjectURL(slot.replacementPreviewUrl);
  }
};

const normalizeDesign = (design) => ({
  designId: design.designId ?? design.DesignId,
  code: design.code ?? design.Code,
  name: design.name ?? design.Name,
  imageUrl_1: design.imageUrl_1 ?? design.ImageUrl_1,
  imageUrl_2: design.imageUrl_2 ?? design.ImageUrl_2,
});

const expandOrderPieces = async (order) => {
  const details = order.orderDetails || order.OrderDetails || [];
  const totalItemsInOrder = details.reduce(
    (sum, d) => sum + Math.max(d.quantity ?? d.Quantity ?? 0, 0),
    0
  );
  const pieces = [];
  let itemIndex = 0;

  for (const detail of details) {
    const qty = Math.max(detail.quantity ?? detail.Quantity ?? 0, 0);
    const designId = detail.designId ?? detail.DesignId;
    if (!designId || qty <= 0) continue;

    let design;
    try {
      design = normalizeDesign(await fetchDesignById(designId));
    } catch {
      continue;
    }
    if (!design?.code) continue;

    for (let unit = 1; unit <= qty; unit += 1) {
      itemIndex += 1;
      pieces.push({
        design,
        orderId: order.orderId ?? order.OrderId,
        itemIndexInOrder: itemIndex,
        totalItemsInOrder,
      });
    }
  }

  return pieces;
};

/**
 * Interactive 2×6 custom print board matching the physical PDF layout.
 */
const CustomPrintBoardModal = ({ open, onClose }) => {
  const [slots, setSlots] = useState(emptyBoard);
  const [activeSlot, setActiveSlot] = useState(0);
  const [swapSourceIndex, setSwapSourceIndex] = useState(null);
  const [searchCode, setSearchCode] = useState("");
  const [orderId, setOrderId] = useState("");
  const [itemIndexInOrder, setItemIndexInOrder] = useState("1");
  const [totalItemsInOrder, setTotalItemsInOrder] = useState("1");
  const [orderSearch, setOrderSearch] = useState("");
  const [orderMatches, setOrderMatches] = useState([]);
  const [orderPieces, setOrderPieces] = useState([]);
  const [loadedOrderLabel, setLoadedOrderLabel] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingOrder, setIsLoadingOrder] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isPrintingLabels, setIsPrintingLabels] = useState(false);
  const [isDownloadingLabelsPdf, setIsDownloadingLabelsPdf] = useState(false);
  const replaceFileInputRef = useRef(null);
  const pendingReplaceSlotRef = useRef(null);

  const filledCount = useMemo(
    () => slots.filter(isSlotFilled).length,
    [slots]
  );

  useEffect(() => {
    if (open) return undefined;
    setSlots((prev) => {
      prev.forEach(revokePreview);
      return emptyBoard();
    });
    setSwapSourceIndex(null);
    setActiveSlot(0);
    pendingReplaceSlotRef.current = null;
    return undefined;
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        setSwapSourceIndex(null);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open]);

  if (!open) return null;

  const findNextEmpty = (fromIndex, board) => {
    for (let i = fromIndex + 1; i < SLOT_COUNT; i += 1) {
      if (!isSlotFilled(board[i])) return i;
    }
    for (let i = 0; i < SLOT_COUNT; i += 1) {
      if (!isSlotFilled(board[i])) return i;
    }
    return fromIndex;
  };

  const placeDesign = (design, meta, preferredSlot = activeSlot) => {
    if (preferredSlot == null || preferredSlot < 0 || preferredSlot >= SLOT_COUNT) {
      toast.warning("اختر موضعًا على لوحة الطباعة أولًا");
      return false;
    }

    const placed = {
      designCode: design.code,
      designId: design.designId,
      name: design.name,
      imageUrl: design.imageUrl_1 || design.imageUrl_2 || null,
      orderId: meta.orderId,
      itemIndexInOrder: meta.itemIndexInOrder,
      totalItemsInOrder: meta.totalItemsInOrder,
    };

    revokePreview(slots[preferredSlot]);

    const projected = slots.map((s, i) => (i === preferredSlot ? placed : s));
    const nextActive = findNextEmpty(preferredSlot, projected);
    setSlots(projected);
    setActiveSlot(nextActive);
    setSwapSourceIndex(null);
    return true;
  };

  const placeManyPieces = (pieces) => {
    if (!pieces.length) {
      toast.warning("لا توجد قطع لوضعها");
      return;
    }

    const next = [...slots];
    let placed = 0;
    let cursor = activeSlot;

    for (const piece of pieces) {
      let target = -1;
      for (let i = cursor; i < SLOT_COUNT; i += 1) {
        if (!isSlotFilled(next[i])) {
          target = i;
          break;
        }
      }
      if (target < 0) {
        for (let i = 0; i < SLOT_COUNT; i += 1) {
          if (!isSlotFilled(next[i])) {
            target = i;
            break;
          }
        }
      }
      if (target < 0) break;

      next[target] = {
        designCode: piece.design.code,
        designId: piece.design.designId,
        name: piece.design.name,
        imageUrl: piece.design.imageUrl_1 || piece.design.imageUrl_2 || null,
        orderId: piece.orderId,
        itemIndexInOrder: piece.itemIndexInOrder,
        totalItemsInOrder: piece.totalItemsInOrder,
      };
      placed += 1;
      cursor = target + 1;
    }

    setSlots(next);
    setActiveSlot(findNextEmpty(Math.max(cursor - 1, 0), next));
    setSwapSourceIndex(null);

    if (placed === 0) {
      toast.warning("اللوحة ممتلئة");
    } else if (placed < pieces.length) {
      toast.warning(`تم وضع ${placed} من ${pieces.length} — المواضع خلصت`);
    } else {
      toast.success(`تم وضع ${placed} قطعة على اللوحة`);
    }
  };

  const clearSlot = (index) => {
    setSlots((prev) => {
      const next = [...prev];
      revokePreview(next[index]);
      next[index] = null;
      return next;
    });
    setActiveSlot(index);
    if (swapSourceIndex === index) setSwapSourceIndex(null);
  };

  const clearBoard = () => {
    setSlots((prev) => {
      prev.forEach(revokePreview);
      return emptyBoard();
    });
    setActiveSlot(0);
    setSwapSourceIndex(null);
  };

  const handleSlotClick = (index) => {
    // Swap / move mode
    if (swapSourceIndex != null) {
      if (swapSourceIndex === index) {
        setSwapSourceIndex(null);
        setActiveSlot(index);
        return;
      }

      setSlots((prev) => {
        const next = [...prev];
        const a = next[swapSourceIndex];
        const b = next[index];
        next[swapSourceIndex] = b;
        next[index] = a;
        return next;
      });
      setSwapSourceIndex(null);
      setActiveSlot(index);
      toast.success(
        slots[index]
          ? `تم التبديل بين الموضع ${swapSourceIndex + 1} و ${index + 1}`
          : `تم نقل القطعة إلى الموضع ${index + 1}`
      );
      return;
    }

    setActiveSlot(index);
  };

  const startSwapFrom = (index, e) => {
    e?.stopPropagation?.();
    if (!isSlotFilled(slots[index])) {
      toast.warning("اختَر قطعة ممتلئة أولًا للتبديل");
      return;
    }
    setSwapSourceIndex(index);
    setActiveSlot(index);
    toast.info("اختر موضع تاني للتبديل أو مكان فاضي للنقل (Esc للإلغاء)");
  };

  const handleReplaceDesign = (index, e) => {
    e?.stopPropagation?.();
    pendingReplaceSlotRef.current = index;
    if (replaceFileInputRef.current) {
      replaceFileInputRef.current.value = "";
      replaceFileInputRef.current.click();
    }
  };

  const handleReplaceFileSelected = (e) => {
    const file = e.target.files?.[0];
    const index = pendingReplaceSlotRef.current;
    e.target.value = "";
    pendingReplaceSlotRef.current = null;

    if (!file || index == null || index < 0 || index >= SLOT_COUNT) return;

    const allowed = ["image/png", "image/jpeg", "image/webp", "image/jpg"];
    const extOk = /\.(png|jpe?g|webp)$/i.test(file.name);
    if (!allowed.includes(file.type) && !extOk) {
      toast.warning("الصورة لازم تكون PNG أو JPG أو WEBP");
      return;
    }

    const previewUrl = URL.createObjectURL(file);
    setSlots((prev) => {
      const next = [...prev];
      const current = next[index] || {
        designCode: null,
        designId: null,
        name: null,
        imageUrl: null,
        orderId: null,
        itemIndexInOrder: 1,
        totalItemsInOrder: 1,
      };
      revokePreview(current);
      next[index] = {
        ...current,
        replacementFile: file,
        replacementPreviewUrl: previewUrl,
        hasCustomPrintImage: true,
      };
      return next;
    });
    setActiveSlot(index);
    toast.success(`تم استبدال تصميم الموضع ${index + 1}`);
  };

  const handleSearch = async (e) => {
    e?.preventDefault?.();
    const code = searchCode.trim();
    if (!code) {
      toast.warning("أدخل كود التصميم");
      return;
    }

    const orderTrimmed = orderId.trim();
    const parsedOrderId = orderTrimmed === "" ? null : Number(orderTrimmed);
    const parsedItemIndex = Number(itemIndexInOrder);
    const parsedTotal = Number(totalItemsInOrder);

    if (orderTrimmed !== "" && (!Number.isFinite(parsedOrderId) || parsedOrderId <= 0)) {
      toast.warning("رقم الأوردر غير صالح");
      return;
    }
    if (!Number.isFinite(parsedItemIndex) || parsedItemIndex <= 0) {
      toast.warning("رقم القطعة مطلوب");
      return;
    }
    if (!Number.isFinite(parsedTotal) || parsedTotal <= 0) {
      toast.warning("إجمالي القطع مطلوب");
      return;
    }
    if (parsedItemIndex > parsedTotal) {
      toast.warning("رقم القطعة لا يمكن أن يتجاوز الإجمالي");
      return;
    }

    setIsSearching(true);
    try {
      const design = normalizeDesign(await fetchDesignByCode(code));
      if (!design?.code) {
        toast.error(`لم يتم العثور على التصميم: ${code}`);
        return;
      }
      const targetSlot = activeSlot;
      const ok = placeDesign(design, {
        orderId: parsedOrderId,
        itemIndexInOrder: parsedItemIndex,
        totalItemsInOrder: parsedTotal,
      });
      if (ok) {
        setSearchCode("");
        toast.success(`تم وضع ${design.code} في الموضع ${targetSlot + 1}`);
      }
    } catch (err) {
      toast.error(err.message || `لم يتم العثور على التصميم: ${code}`);
    } finally {
      setIsSearching(false);
    }
  };

  const loadOrderIntoPicker = async (order) => {
    const pieces = await expandOrderPieces(order);
    if (!pieces.length) {
      toast.warning("هذا الأوردر مفيهوش قطع صالحة");
      setOrderPieces([]);
      return;
    }
    const oid = order.orderId ?? order.OrderId;
    const name = order.name ?? order.Name ?? "";
    setOrderPieces(pieces);
    setLoadedOrderLabel(`#${oid}${name ? ` — ${name}` : ""}`);
    setOrderMatches([]);
    toast.success(`تم تحميل ${pieces.length} قطعة من الأوردر #${oid}`);
  };

  const handleOrderSearch = async (e) => {
    e?.preventDefault?.();
    const term = orderSearch.trim();
    if (!term) {
      toast.warning("أدخل رقم الأوردر أو رقم الموبايل");
      return;
    }

    setIsLoadingOrder(true);
    setOrderMatches([]);
    setOrderPieces([]);
    setLoadedOrderLabel("");

    try {
      if (/^\d+$/.test(term) && term.length <= 8) {
        // Treat short numeric as order id
        const order = await fetchOrderById(term);
        await loadOrderIntoPicker(order);
      } else if (/^\d+$/.test(term)) {
        // Longer numeric: try order id first, then phone
        try {
          const order = await fetchOrderById(term);
          await loadOrderIntoPicker(order);
        } catch {
          const matches = await searchOrdersByPhone(term);
          if (!matches?.length) {
            toast.error("لا توجد نتائج");
            return;
          }
          if (matches.length === 1) {
            const full = await fetchOrderById(matches[0].orderId ?? matches[0].OrderId);
            await loadOrderIntoPicker(full);
          } else {
            setOrderMatches(matches);
            toast.info(`وجدنا ${matches.length} أوردر — اختَر واحد`);
          }
        }
      } else {
        const matches = await searchOrdersByPhone(term);
        if (!matches?.length) {
          toast.error("لا توجد نتائج لهذا الرقم");
          return;
        }
        if (matches.length === 1) {
          const full = await fetchOrderById(matches[0].orderId ?? matches[0].OrderId);
          await loadOrderIntoPicker(full);
        } else {
          setOrderMatches(matches);
          toast.info(`وجدنا ${matches.length} أوردر — اختَر واحد`);
        }
      }
    } catch (err) {
      toast.error(err.message || "فشل البحث عن الأوردر");
    } finally {
      setIsLoadingOrder(false);
    }
  };

  const pickOrderMatch = async (match) => {
    setIsLoadingOrder(true);
    try {
      const full = await fetchOrderById(match.orderId ?? match.OrderId);
      await loadOrderIntoPicker(full);
    } catch (err) {
      toast.error(err.message || "فشل تحميل الأوردر");
    } finally {
      setIsLoadingOrder(false);
    }
  };

  const handleGenerate = async () => {
    if (filledCount === 0) {
      toast.warning("ضع تصميمًا واحدًا على الأقل قبل إنشاء الـ PDF");
      return;
    }

    setIsGenerating(true);
    try {
      const payload = slots.map((s) => s?.designCode || null);
      const slotFiles = {};
      slots.forEach((s, i) => {
        if (s?.replacementFile) slotFiles[i] = s.replacementFile;
      });
      const result = await generateCustomPrintPdf(payload, slotFiles);
      toast.success(`تم تحميل ${result.fileName}`);
    } catch (err) {
      const missing = err.missingDesignCodes?.length
        ? ` — الناقص: ${err.missingDesignCodes.join(", ")}`
        : "";
      toast.error((err.message || "فشل إنشاء PDF المخصص") + missing);
    } finally {
      setIsGenerating(false);
    }
  };

  const handlePrintLabels = async () => {
    const filled = slots
      .map((item, index) => ({ item, slotNum: index + 1 }))
      .filter(({ item }) => isSlotFilled(item));

    if (filled.length === 0) {
      toast.warning("لا توجد مواضع ممتلئة لطباعة ملصقات");
      return;
    }

    setIsPrintingLabels(true);
    try {
      const config = await createLabelPrinterConfig();
      const data = filled.map(({ item, slotNum }) => ({
        type: "raw",
        data: buildTsplFromLines(buildCustomBoardLabelLines(item, slotNum)),
      }));

      await qz.print(config, data);
      toast.success(`تم طباعة ${data.length} ملصق`);
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "فشل طباعة الملصقات");
    } finally {
      setIsPrintingLabels(false);
    }
  };

  const handleDownloadLabelsPdf = () => {
    const filled = slots
      .map((item, index) => ({ item, slotNum: index + 1 }))
      .filter(({ item }) => isSlotFilled(item));

    if (filled.length === 0) {
      toast.warning("لا توجد مواضع ممتلئة لتحميل ملصقات");
      return;
    }

    setIsDownloadingLabelsPdf(true);
    try {
      const count = downloadLabelsPdf(
        filled.map(({ item, slotNum }) => buildCustomBoardLabelLines(item, slotNum)),
        `labels-custom-${Date.now()}.pdf`
      );
      toast.success(`تم تحميل PDF ${count} ملصق`);
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "فشل إنشاء PDF الملصقات");
    } finally {
      setIsDownloadingLabelsPdf(false);
    }
  };

  const busy = isSearching || isGenerating || isPrintingLabels || isDownloadingLabelsPdf || isLoadingOrder;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
      <div
        dir="rtl"
        className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[92vh] overflow-hidden flex flex-col"
      >
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
              <LayoutGrid size={20} className="text-indigo-600" />
              Custom Print PDF
            </h2>
            <p className="text-sm text-slate-500 mt-0.5">
              لوحة طباعة ٢ × ٦ — نفس تخطيط الـ PDF ({filledCount}/12 ممتلئ)
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-slate-100 text-slate-500 transition"
            title="إغلاق"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-5 overflow-y-auto flex-1 space-y-5">
          <input
            ref={replaceFileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,.png,.jpg,.jpeg,.webp"
            className="hidden"
            onChange={handleReplaceFileSelected}
          />
          {/* Order search */}
          <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-4 space-y-3">
            <div className="flex items-center gap-2 text-amber-900 font-semibold text-sm">
              <Package size={16} />
              البحث عن اوردر (موبايل أو رقم أوردر)
            </div>
            <form onSubmit={handleOrderSearch} className="flex gap-2 flex-wrap">
              <div className="relative flex-1 min-w-[200px]">
                <Search
                  size={16}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                />
                <input
                  type="text"
                  value={orderSearch}
                  onChange={(e) => setOrderSearch(e.target.value)}
                  placeholder="رقم الأوردر أو رقم الموبايل..."
                  className="w-full pr-10 pl-3 py-2.5 rounded-xl border border-amber-200 bg-white focus:outline-none focus:ring-2 focus:ring-amber-400 font-mono text-sm"
                  dir="ltr"
                />
              </div>
              <button
                type="submit"
                disabled={busy}
                className="px-4 py-2.5 bg-amber-600 hover:bg-amber-700 text-white rounded-xl font-semibold text-sm transition disabled:opacity-50 flex items-center gap-2"
              >
                {isLoadingOrder ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Search size={16} />
                )}
                بحث
              </button>
            </form>

            {orderMatches.length > 0 && (
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {orderMatches.map((m) => {
                  const oid = m.orderId ?? m.OrderId;
                  return (
                    <button
                      key={oid}
                      type="button"
                      onClick={() => pickOrderMatch(m)}
                      disabled={busy}
                      className="w-full text-right px-3 py-2 rounded-xl bg-white border border-amber-200 hover:border-amber-400 text-sm transition"
                    >
                      <span className="font-bold text-amber-800">#{oid}</span>
                      <span className="text-slate-600 mx-2">
                        {m.name ?? m.Name}
                      </span>
                      <span className="text-slate-400 font-mono text-xs" dir="ltr">
                        {m.phoneNumber ?? m.PhoneNumber}
                      </span>
                      <span className="text-xs text-slate-500 mr-2">
                        ({m.status ?? m.Status})
                      </span>
                    </button>
                  );
                })}
              </div>
            )}

            {orderPieces.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <p className="text-sm font-medium text-slate-700">
                    قطع الأوردر {loadedOrderLabel} ({orderPieces.length})
                  </p>
                  <button
                    type="button"
                    onClick={() => placeManyPieces(orderPieces)}
                    disabled={busy}
                    className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
                  >
                    ضع الكل في المواضع الفاضية
                  </button>
                </div>
                <div className="flex gap-2 overflow-x-auto pb-1">
                  {orderPieces.map((piece) => {
                    const img =
                      piece.design.imageUrl_1 || piece.design.imageUrl_2;
                    return (
                      <button
                        key={`${piece.orderId}-${piece.itemIndexInOrder}`}
                        type="button"
                        disabled={busy}
                        onClick={() => {
                          const ok = placeDesign(piece.design, {
                            orderId: piece.orderId,
                            itemIndexInOrder: piece.itemIndexInOrder,
                            totalItemsInOrder: piece.totalItemsInOrder,
                          });
                          if (ok) {
                            toast.success(
                              `تم وضع ${piece.design.code} (${piece.itemIndexInOrder}/${piece.totalItemsInOrder})`
                            );
                          }
                        }}
                        className="shrink-0 w-24 rounded-xl border border-slate-200 bg-white hover:border-indigo-400 transition overflow-hidden text-center"
                        title={`وضع في الموضع ${activeSlot + 1}`}
                      >
                        <div className="aspect-square bg-slate-50 flex items-center justify-center">
                          {img ? (
                            <img
                              src={`${API_BASE_URL_V2}${img}`}
                              alt={piece.design.code}
                              className="max-w-full max-h-full object-contain"
                            />
                          ) : (
                            <span className="text-[10px] text-slate-400 px-1">
                              {piece.design.code}
                            </span>
                          )}
                        </div>
                        <div className="px-1 py-1 border-t border-slate-100">
                          <p className="text-[10px] font-bold text-indigo-700 font-mono truncate">
                            {piece.design.code}
                          </p>
                          <p className="text-[9px] text-slate-500 font-mono">
                            {piece.itemIndexInOrder}/{piece.totalItemsInOrder}
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Design code place */}
          <form onSubmit={handleSearch} className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
              <div className="relative sm:col-span-2 lg:col-span-1">
                <Search
                  size={16}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                />
                <input
                  type="text"
                  value={searchCode}
                  onChange={(e) => setSearchCode(e.target.value.toUpperCase())}
                  placeholder="كود التصميم *"
                  className="w-full pr-10 pl-3 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono text-sm"
                  dir="ltr"
                />
              </div>
              <input
                type="number"
                min="1"
                value={orderId}
                onChange={(e) => setOrderId(e.target.value)}
                placeholder="رقم الأوردر (اختياري)"
                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono text-sm"
                dir="ltr"
              />
              <input
                type="number"
                min="1"
                value={itemIndexInOrder}
                onChange={(e) => setItemIndexInOrder(e.target.value)}
                placeholder="رقم القطعة"
                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono text-sm"
                dir="ltr"
              />
              <input
                type="number"
                min="1"
                value={totalItemsInOrder}
                onChange={(e) => setTotalItemsInOrder(e.target.value)}
                placeholder="إجمالي القطع"
                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono text-sm"
                dir="ltr"
              />
            </div>
            <button
              type="submit"
              disabled={busy}
              className="w-full sm:w-auto px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-semibold text-sm transition disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isSearching ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Search size={16} />
              )}
              وضع في الموضع {activeSlot + 1}
            </button>
          </form>

          {swapSourceIndex != null && (
            <div className="flex items-center justify-between gap-3 px-3 py-2 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-sm">
              <span className="flex items-center gap-2 font-medium">
                <ArrowLeftRight size={16} />
                جاري التبديل من الموضع {swapSourceIndex + 1} — اختَر موضع تاني أو فاضي. Esc
                للإلغاء
              </span>
              <button
                type="button"
                onClick={() => setSwapSourceIndex(null)}
                className="text-xs font-semibold px-2 py-1 rounded-lg hover:bg-amber-100"
              >
                إلغاء
              </button>
            </div>
          )}

          <p className="text-xs text-slate-500">
            الترتيب من اليمين لليسار زي الطباعة (١ على اليمين). اضغط زر التبديل على قطعة ثم اختَر
            قطعة تانية أو مكان فاضي. زر الصورة الخضراء لتعديل تصميم الموضع. طباعة الملصقات للمواضع الممتلئة فقط.
          </p>

          <div
            className="grid gap-2 md:gap-3"
            style={{ gridTemplateColumns: `repeat(${COLUMNS}, minmax(0, 1fr))` }}
            dir="rtl"
          >
            {slots.map((slot, index) => {
              const isActive = activeSlot === index;
              const isSwapSource = swapSourceIndex === index;
              const isSwapTarget =
                swapSourceIndex != null && swapSourceIndex !== index;
              const imageSrc = slot?.replacementPreviewUrl
                ? slot.replacementPreviewUrl
                : slot?.imageUrl
                  ? `${API_BASE_URL_V2}${slot.imageUrl}`
                  : null;

              return (
                <button
                  key={`custom-slot-${index}`}
                  type="button"
                  onClick={() => handleSlotClick(index)}
                  className={`relative aspect-[1/2] rounded-xl border-2 overflow-hidden transition text-left ${
                    isSwapSource
                      ? "border-amber-500 ring-2 ring-amber-300"
                      : isSwapTarget
                        ? "border-emerald-400 ring-2 ring-emerald-200"
                        : isActive
                          ? "border-indigo-500 ring-2 ring-indigo-200"
                          : isSlotFilled(slot)
                            ? "border-slate-300 hover:border-indigo-300"
                            : "border-dashed border-slate-200 bg-slate-50 hover:border-slate-300"
                  }`}
                >
                  <span className="absolute top-1.5 left-1.5 z-10 bg-slate-900/80 text-white text-xs font-bold px-1.5 py-0.5 rounded">
                    {index + 1}
                  </span>

                  {isSlotFilled(slot) ? (
                    <>
                      {slot.hasCustomPrintImage && (
                        <span
                          className="absolute top-8 left-1.5 z-10 bg-emerald-600 text-white text-[9px] font-bold px-1.5 py-0.5 rounded"
                          title="تم استبدال صورة الطباعة"
                        >
                          معدّل
                        </span>
                      )}
                      <div className="h-full w-full bg-white flex items-center justify-center">
                        {imageSrc ? (
                          <img
                            src={imageSrc}
                            alt={slot.designCode || "custom"}
                            className="max-w-full max-h-full object-contain"
                          />
                        ) : (
                          <span className="text-xs text-slate-400 px-2 text-center">
                            {slot.designCode || "صورة مخصصة"}
                          </span>
                        )}
                      </div>
                      <div className="absolute bottom-0 inset-x-0 bg-white/95 border-t border-slate-100 px-1.5 py-1">
                        <p className="text-[11px] font-bold text-indigo-700 truncate text-center font-mono">
                          {slot.designCode || "CUSTOM"}
                        </p>
                        <p className="text-[10px] text-slate-500 truncate text-center font-mono">
                          {slot.orderId != null ? `#${slot.orderId} · ` : ""}
                          {slot.itemIndexInOrder}/{slot.totalItemsInOrder}
                        </p>
                      </div>
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => {
                          e.stopPropagation();
                          clearSlot(index);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.stopPropagation();
                            clearSlot(index);
                          }
                        }}
                        className="absolute top-1.5 right-1.5 z-10 p-1 rounded-md bg-red-500 text-white hover:bg-red-600"
                        title="إزالة"
                      >
                        <Trash2 size={12} />
                      </span>
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => handleReplaceDesign(index, e)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            handleReplaceDesign(index, e);
                          }
                        }}
                        className="absolute top-8 right-1.5 z-10 p-1 rounded-md bg-emerald-600 text-white hover:bg-emerald-700"
                        title="تعديل التصميم"
                      >
                        <ImagePlus size={12} />
                      </span>
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => startSwapFrom(index, e)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            startSwapFrom(index, e);
                          }
                        }}
                        className="absolute top-1.5 right-8 z-10 p-1 rounded-md bg-amber-500 text-white hover:bg-amber-600"
                        title="تبديل / نقل"
                      >
                        <ArrowLeftRight size={12} />
                      </span>
                    </>
                  ) : (
                    <div className="h-full w-full flex flex-col items-center justify-center text-slate-400 gap-1">
                      <LayoutGrid size={22} className="opacity-40" />
                      <span className="text-xs">
                        {isSwapTarget ? "انقل هنا" : "فارغ"}
                      </span>
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => handleReplaceDesign(index, e)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            handleReplaceDesign(index, e);
                          }
                        }}
                        className="mt-1 p-1 rounded-md bg-emerald-600 text-white hover:bg-emerald-700"
                        title="تعديل التصميم"
                      >
                        <ImagePlus size={12} />
                      </span>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div className="px-5 py-4 border-t border-slate-100 flex flex-wrap items-center justify-between gap-3 bg-slate-50/80">
          <button
            type="button"
            onClick={clearBoard}
            disabled={busy || filledCount === 0}
            className="px-4 py-2 rounded-xl text-sm font-medium text-slate-600 hover:bg-white border border-slate-200 transition disabled:opacity-40"
          >
            تفريغ اللوحة
          </button>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl text-sm font-medium bg-white border border-slate-200 hover:bg-slate-50 transition"
            >
              إلغاء
            </button>
            <button
              type="button"
              onClick={handlePrintLabels}
              disabled={busy || filledCount === 0}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold bg-indigo-600 hover:bg-indigo-700 text-white transition disabled:opacity-50"
            >
              {isPrintingLabels ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Tag size={16} />
              )}
              {isPrintingLabels ? "جاري الطباعة..." : "طباعة الملصقات"}
            </button>
            <button
              type="button"
              onClick={handleDownloadLabelsPdf}
              disabled={busy || filledCount === 0}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold bg-white border border-indigo-200 text-indigo-700 hover:bg-indigo-50 transition disabled:opacity-50"
            >
              {isDownloadingLabelsPdf ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <FileDown size={16} />
              )}
              {isDownloadingLabelsPdf ? "جاري إنشاء PDF..." : "تنزيل PDF الملصقات"}
            </button>
            <button
              type="button"
              onClick={handleGenerate}
              disabled={busy || filledCount === 0}
              className="flex items-center gap-2 px-5 py-2 rounded-xl text-sm font-semibold bg-emerald-600 hover:bg-emerald-700 text-white transition disabled:opacity-50"
            >
              {isGenerating ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <FileDown size={16} />
              )}
              {isGenerating ? "جاري الإنشاء..." : "Generate Custom PDF"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomPrintBoardModal;
